import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getMe, loginEmail, registerSurgeon, logout as doLogout } from "@/lib/data";

const AuthContext = createContext(null);

// Backwards-compat helper used by pages that still pass FastAPI-shaped errors.
// Most callers can now just use `error.message` directly.
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = bootstrapping, false = anonymous, object = signed-in profile
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = async () => {
    try {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess?.session) {
        setUser(false);
        return null;
      }
      const profile = await getMe();
      setUser(profile ?? false);
      return profile;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const applySession = async (session) => {
      if (cancelled) return;
      if (!session) {
        setUser(false);
        setLoading(false);
        return;
      }
      try {
        const profile = await getMe();
        if (cancelled) return;
        setUser(profile ?? false);
      } catch {
        // Keep the session but show a minimal user object so ProtectedRoute
        // does not bounce us out on a transient profile fetch failure
        // (e.g. right after returning from Stripe).
        if (!cancelled) {
          setUser((prev) =>
            prev && typeof prev === "object"
              ? prev
              : { id: session.user.id, email: session.user.email },
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Subscribe FIRST so we receive the INITIAL_SESSION event that Supabase
    // fires immediately after it rehydrates the session from localStorage.
    // This is the reliable signal that auth is ready - racing against
    // getSession() directly is what caused the post-Stripe auto-logout.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "SIGNED_OUT") {
        setUser(false);
        setLoading(false);
        return;
      }
      if (
        event === "INITIAL_SESSION" ||
        event === "SIGNED_IN" ||
        event === "TOKEN_REFRESHED" ||
        event === "USER_UPDATED"
      ) {
        applySession(session);
      }
    });

    // Safety net: if onAuthStateChange never fires (shouldn't happen, but
    // we don't want a hung splash screen forever), resolve after 8s.
    const safety = setTimeout(() => {
      if (!cancelled && loading) {
        supabase.auth.getSession().then(({ data }) => applySession(data?.session ?? null));
      }
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(safety);
      sub?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    await loginEmail(email, password);
    const profile = await getMe();
    setUser(profile ?? false);
    return profile;
  };

  const register = async (payload) => {
    // Edge Function creates the user + profile and emails the OTP.
    const result = await registerSurgeon(payload);
    return { ...result, requires_verification: true };
  };

  const logout = async () => {
    try {
      await doLogout();
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

